import {
  faBars,
  faCompressAlt,
  faExpandAlt,
  faSignInAlt,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, lazy, Suspense, useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { Button } from 'src/components/button';
import { Container } from 'src/components/container';
import { Dropdown } from 'src/components/dropdown';
import {
  HeaderPrimaryBackground,
  HeaderPrimaryCenter,
} from 'src/components/header/primary-nav';
import { ActiveSection } from 'src/components/header/types';
import { ExternalLink, Link } from 'src/components/link';
import { LoginModal } from 'src/components/login-modal';
import { Logo } from 'src/components/logo';
import { Stack } from 'src/components/stack';
import { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useCognitoUser } from 'src/hooks/use-cognito';
import { useFeatureFlags } from 'src/hooks/use-feature-flags';
import { useFullwidth } from 'src/hooks/use-fullwidth';
import { useGmdFlags } from 'src/hooks/use-gdm-flags';
import { useLogout } from 'src/hooks/use-logout';
import { useOptionalMonolithCurrentUser } from 'src/hooks/use-monolith-user';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { OrganisationServiceRequest } from 'src/pages/settings/organisation/use-organisation-service';
import { Colors } from 'src/styles';
import { isLng } from 'src/utils/is-lng';
import styled from 'styled-components';

const MobileNav = styled.div`
  display: grid;
  grid-gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 0.1rem solid ${Colors.brand};
`;

const DeveloperToolsIcon = lazy(() => import('src/pages/developer-tools/icon'));

type Props = {
  activeSection: ActiveSection;
  organisationService?: Successful<OrganisationServiceRequest>;
};

export const HeaderPrimary: FC<Props> = ({
  activeSection,
  organisationService,
}) => {
  const [fullwidth, setFullwidth] = useFullwidth();
  const [showLogin, setShowLogin] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const { minTablet, minDesktop, minWideDesktop } = useBreakpoints();
  const auth = useOptionalMonolithCurrentUser();
  const user = useCognitoUser();
  const isAuthenticated = Boolean(auth || user.data);
  const logout = useLogout();
  const featureFlags = useFeatureFlags();
  const gdmFlags = useGmdFlags();
  const isGdmSection = useRouteMatch('/gdm');
  const { pathname, search } = useLocation();
  const [reauthenticate] = useReauthenticate();
  const { userInvitations } = useFeatureFlags();

  const sectionLinks = (
    <>
      {(!auth || auth.role !== 'SSO_ADMIN') && (
        <ExternalLink
          mode="primary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/auctions`}
          isActive={activeSection.transport}
        >
          Transport
        </ExternalLink>
      )}

      {(!auth ||
        (auth.role === 'SSO_ADMIN'
          ? !isLng(auth)
          : auth.role !== 'TSO_USER')) && (
        <ExternalLink
          mode="primary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/offers`}
          isActive={activeSection.storage}
        >
          Storage
        </ExternalLink>
      )}

      {(!auth ||
        (auth.role === 'SSO_ADMIN'
          ? isLng(auth)
          : auth.role !== 'TSO_USER')) && (
        <ExternalLink
          mode="primary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/lng/offers`}
          isActive={activeSection.lng}
        >
          LNG
        </ExternalLink>
      )}

      {(!auth || auth.role !== 'SSO_ADMIN') && (
        <Link mode="primary" to="/reporting" isActive={activeSection.reporting}>
          Reporting
        </Link>
      )}

      <Link mode="primary" to="/umm">
        UMM
      </Link>

      {(gdmFlags.userCanAcces || isGdmSection) && (
        <Link mode="primary" to="/gdm" isActive={activeSection.gdm}>
          GDM
        </Link>
      )}

      <Link mode="primary" to="/aggregate-eu">
        AggregateEU
      </Link>
    </>
  );

  const authLinks =
    isAuthenticated && !reauthenticate ? (
      <>
        {featureFlags.authMigration ? (
          <Link
            mode={minTablet ? 'dropdown' : 'primary'}
            to="/settings/profile"
          >
            Profile
          </Link>
        ) : (
          <ExternalLink
            mode={minTablet ? 'dropdown' : 'primary'}
            href={`${PRISMA_CONFIG.oldFrontendUrl}${
              auth?.role === 'TSO_USER' || auth?.role === 'SSO_ADMIN'
                ? '/#/tso-user'
                : '/#/platform-settings'
            }`}
          >
            Profile
          </ExternalLink>
        )}

        <Link
          mode={minTablet ? 'dropdown' : 'primary'}
          to="/settings/notifications"
        >
          Notification Settings
        </Link>

        {userInvitations &&
        organisationService?.response.data._links.getUserDetails ? (
          <Link
            mode={minTablet ? 'dropdown' : 'primary'}
            to="/settings/organisation/users"
            data-testid="user-management-link"
          >
            User Management
          </Link>
        ) : null}

        {auth?.proxyShipper && (
          <ExternalLink
            mode={minTablet ? 'dropdown' : 'primary'}
            href={`${PRISMA_CONFIG.monolithUrl}/_exit_user`}
          >
            Switch to {auth.originalRole === 'SSO_ADMIN' ? 'SSO' : 'TSO'} Role
          </ExternalLink>
        )}

        {(auth?.role === 'TSO_USER' || auth?.role === 'SSO_ADMIN') &&
          auth.hasShipperRole &&
          !auth.hasAwaitingShipperRole && (
            <ExternalLink
              mode={minTablet ? 'dropdown' : 'primary'}
              href={`${PRISMA_CONFIG.monolithUrl}/_switch_user`}
            >
              Switch to Shipper Role
            </ExternalLink>
          )}

        {(auth?.role === 'TSO_USER' || auth?.role === 'SSO_ADMIN') &&
          auth.hasShipperRole &&
          auth.hasAwaitingShipperRole && (
            <Button mode={minTablet ? 'dropdown' : 'link-inverse-alt'} disabled>
              Shipper Role Awaiting Activation
            </Button>
          )}

        {featureFlags.authMigration ? (
          <Link mode={minTablet ? 'dropdown' : 'inverse-alt'} to="/logout">
            Logout
          </Link>
        ) : (
          <Button
            mode={minTablet ? 'dropdown' : 'link-inverse-alt'}
            onClick={logout.execute}
            disabled={logout.pending}
          >
            Logoff
          </Button>
        )}
      </>
    ) : (
      <>
        {!reauthenticate && (
          <Link mode="primary" to="/registration/wizard/start">
            Register
          </Link>
        )}

        {featureFlags.authMigration ? (
          <Link
            mode={minTablet ? 'button-inverse' : 'inverse-alt'}
            to={
              // if someone presses on the login button from _within_ the login
              // section, we just want to reuse the search params
              pathname.startsWith('/login')
                ? { pathname: '/login', search }
                : {
                    pathname: '/login',
                    search: `redirectUrl=${encodeURIComponent(location.href)}`,
                  }
            }
          >
            {minTablet && !minDesktop ? (
              <FontAwesomeIcon icon={faSignInAlt} aria-label="Login" />
            ) : (
              <>Login</>
            )}
          </Link>
        ) : (
          <Button
            mode={minTablet ? 'inverse' : 'link-inverse-alt'}
            onClick={() => setShowLogin(true)}
          >
            {minTablet && !minDesktop ? (
              <FontAwesomeIcon icon={faSignInAlt} aria-label="Login" />
            ) : (
              <>Login</>
            )}
          </Button>
        )}
      </>
    );

  const developerTools = useDeveloperTools();

  return (
    <HeaderPrimaryBackground>
      <Container>
        <HeaderPrimaryCenter>
          <Stack flow="column" gap={3.4}>
            <RouterLink to="/platform/#/start">
              <Logo />
            </RouterLink>

            {minTablet && sectionLinks}
          </Stack>

          <Stack flow="column" gap={2}>
            {!minTablet && (
              <Button onClick={() => setShowMobileNav(!showMobileNav)}>
                <FontAwesomeIcon
                  icon={faBars}
                  aria-label="Toggle Main Navigation"
                />
              </Button>
            )}

            {minTablet &&
              (isAuthenticated && !reauthenticate ? (
                <Dropdown
                  data-testid="toggle-user-menu"
                  icon={faUser}
                  label="Toggle User Menu"
                >
                  {authLinks}
                </Dropdown>
              ) : (
                authLinks
              ))}

            {minWideDesktop && (
              <Button
                mode="icon-inverse"
                onClick={() => setFullwidth(!fullwidth)}
              >
                {fullwidth ? (
                  <FontAwesomeIcon
                    icon={faCompressAlt}
                    aria-label="Decrease width of website"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faExpandAlt}
                    aria-label="Increase width of website"
                  />
                )}
              </Button>
            )}

            {minWideDesktop && developerTools && (
              <Suspense fallback={<div style={{ width: 18 }} />}>
                <DeveloperToolsIcon />
              </Suspense>
            )}
          </Stack>
        </HeaderPrimaryCenter>
      </Container>

      {!minTablet && showMobileNav && (
        <MobileNav>
          {authLinks}
          {sectionLinks}
        </MobileNav>
      )}

      {showLogin && <LoginModal onDismiss={() => setShowLogin(false)} />}
    </HeaderPrimaryBackground>
  );
};
